/**
 * Opening a new window exposes users to security exploit where malicious
 * parties can modify their browsers' "back history", leading to phishing
 * attacks.
 *
 * This adds the "noopener" option which prevents that exploit.
 */
import { isObservable, Observable, of } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { DailyAvailability, TimeRange } from './interface';
import { AvailabilityRule, DayOfWeek } from '@vendasta/meetings';
// @ts-expect-error Angular 21 doesn't like imports past the base package
import { AvailabilityRuleList } from '@vendasta/meetings/lib';

export function openSecureNewTab(url: string): Window {
  // tslint:disable-next-line:ban
  return window.open(url, '_blank', 'noopener');
}

export function openSecureCurrentTab(url: string): Window {
  // tslint:disable-next-line:ban
  return window.open(url, undefined, 'noopener');
}

export function getObservableString(v: string | undefined | null | Observable<string>): Observable<string> {
  return isObservable(v) ? v : of(v || '');
}

export function loadTimeOptions(): Date[] {
  const today = new Date();
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    timeOptions.push(
      new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, 0, 0),
      new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, 15, 0),
      new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, 30, 0),
      new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, 45, 0),
    );
  }
  return timeOptions;
}

export function getTimeOption(timeOptions: Date[], hours: number, minutes = 0): Date {
  return timeOptions[(hours || 0) * 4 + Math.floor(minutes / 15)];
}

/**
 * Transform API AvailabilityRule[] into UI DailyAvailability format.
 * Uses predefined time options so Date references match component dropdowns.
 * @param rules Rules from listAvailability / getEventTypeById response
 * @param timeOptions Optional; if not provided, loadTimeOptions() is used
 */
export function availabilityRulesToDailyAvailability(
  rules: AvailabilityRule[],
  timeOptions?: Date[],
): DailyAvailability {
  const options = timeOptions ?? loadTimeOptions();
  const days = new Map<DayOfWeek, TimeRange[]>();

  const isValidTime = (hours: number, minutes: number): boolean =>
    hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;

  for (const rule of rules) {
    const day = rule.day;
    const fromTime = rule.timeSlot.from;
    const toTime = rule.timeSlot.to;
    const fromMinutes = fromTime.minutes ?? 0;
    const toMinutes = toTime.minutes ?? 0;

    if (!isValidTime(fromTime.hours, fromMinutes) || !isValidTime(toTime.hours, toMinutes)) {
      continue;
    }

    const start = getTimeOption(options, fromTime.hours, fromMinutes);
    const end = getTimeOption(options, toTime.hours, toMinutes);
    if (!start || !end) {
      continue;
    }

    const timeRange: TimeRange = { start, end };
    const existing = days.get(day);
    if (existing) {
      existing.push(timeRange);
    } else {
      days.set(day, [timeRange]);
    }
  }

  days.forEach((ranges, day) => {
    days.set(
      day,
      ranges.sort((a, b) => a.start.getTime() - b.start.getTime()),
    );
  });

  return { days };
}

export function defaultTimeRange(): TimeRange {
  const timeOptions = loadTimeOptions();
  return { start: getTimeOption(timeOptions, 9), end: getTimeOption(timeOptions, 17) };
}

export function getDefaultAvailability(): DailyAvailability {
  const defaultTime = defaultTimeRange();
  const days = new Map<DayOfWeek, TimeRange[]>([
    [DayOfWeek.MONDAY, [{ ...defaultTime }]],
    [DayOfWeek.TUESDAY, [{ ...defaultTime }]],
    [DayOfWeek.WEDNESDAY, [{ ...defaultTime }]],
    [DayOfWeek.THURSDAY, [{ ...defaultTime }]],
    [DayOfWeek.FRIDAY, [{ ...defaultTime }]],
  ]);
  return { days };
}

/**
 * Returns the common available time slots for the given day across selected hosts.
 * @param day DayOfWeek
 * @param hostAvailability Map<string, AvailabilityRuleList>
 * @param selectedHosts string[]
 * @returns Array of { from: { hours, minutes }, to: { hours, minutes } }
 */
export function getCommonTimeSlots(
  day: DayOfWeek,
  hostAvailability: Map<string, AvailabilityRuleList>,
  selectedHosts: string[],
): Array<{ from: { hours: number; minutes: number }; to: { hours: number; minutes: number } }> {
  // Helper to get time ranges for a user on a specific day
  const getTimeRanges = (userData: any, day: number) => {
    if (!userData || !userData.rules) return [];
    return userData.rules
      .filter((rule: any) => rule.day === day)
      .map((rule: any) => {
        const from = rule.timeSlot.from;
        const to = rule.timeSlot.to;
        return {
          from: {
            hours: from.hours,
            minutes: from.minutes || 0,
          },
          to: {
            hours: to.hours,
            minutes: to.minutes || 0,
          },
        };
      });
  };

  // Convert time to minutes since 00:00
  const toMinutes = (time: { hours: number; minutes: number }) => time.hours * 60 + (time.minutes || 0);

  // Convert minutes to time object
  const toTime = (minutes: number) => ({
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
  });

  // Intersect two lists of time ranges
  const intersectRanges = (a: any[], b: any[]) => {
    const result = [];
    for (const r1 of a) {
      for (const r2 of b) {
        const start = Math.max(toMinutes(r1.from), toMinutes(r2.from));
        const end = Math.min(toMinutes(r1.to), toMinutes(r2.to));
        if (start < end) {
          result.push({ from: toTime(start), to: toTime(end) });
        }
      }
    }
    return result;
  };

  if (!selectedHosts.length) return [];

  // Get all ranges from the first user
  let commonRanges = getTimeRanges(hostAvailability.get(selectedHosts[0]), day);

  // Intersect with the rest
  for (let i = 1; i < selectedHosts.length; i++) {
    const userRanges = getTimeRanges(hostAvailability.get(selectedHosts[i]), day);
    commonRanges = intersectRanges(commonRanges, userRanges);
    if (commonRanges.length === 0) break; // early exit if no overlap
  }

  return commonRanges;
}

/**
 * Get the icon URL for meeting integration services
 * @param iconName The name of the icon (meet-icon, zoom-icon, teams-icon)
 * @returns The URL of the icon, or empty string if not found
 */
export function getMeetingIntegrationIconUrl(iconName: string): string {
  const iconMap: { [key: string]: string } = {
    'meet-icon': 'https://storage.googleapis.com/meeting-scheduler-prod-public-images/google-hangouts-meet.svg',
    'zoom-icon': 'https://storage.googleapis.com/meeting-scheduler-prod-public-images/zoom-icon.svg',
    'teams-icon': 'https://storage.googleapis.com/meeting-scheduler-prod-public-images/teams-icon.svg',
  };
  return iconMap[iconName] || '';
}

/**
 * Determine if the component is in settings mode vs setup wizard mode
 * @param route The ActivatedRoute to check for query parameters
 * @returns true if in settings mode, false if in setup wizard mode
 */
export function isInSettingsMode(route: ActivatedRoute): boolean {
  const wizardParam = route.snapshot.queryParams['wizard'];
  return wizardParam !== 'true';
}
